"use client";
import React, { useState } from "react";
import { Eye, EyeOff, Check, Mail, Lock, User, Building2, Globe, MapPin, Phone } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectItemIndicator,
} from "../../components/ui/select";
import { register } from "../../services/auth";
import { APIAddressSelect } from "../../components/ui/APIAddressSelect";
import { useToast } from "../../hooks/useToast";

export default function Signup() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const showToast = useToast();

  // Form state
  const [businessName, setBusinessName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [callingCode, setCallingCode] = useState("+44");
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [category, setCategory] = useState("1");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const errors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const subdomainRegex = /^[a-zA-Z0-9-]+$/;
    const phoneRegex = /^\d+$/;

    if (!businessName.trim()) errors.businessName = "Required";
    if (!subdomain.trim()) {
      errors.subdomain = "Required";
    } else if (!subdomainRegex.test(subdomain)) {
      errors.subdomain = "Invalid format";
    }

    if (!contact.trim()) {
      errors.contact = "Required";
    } else if (!phoneRegex.test(contact)) {
      errors.contact = "Digits only";
    }

    if (!address.trim() || address.trim().length < 5) errors.address = "Valid address required";
    if (!name.trim()) errors.name = "Required";

    if (!email) {
      errors.email = "Required";
    } else if (!emailRegex.test(email)) {
      errors.email = "Invalid format";
    }

    if (!password) {
      errors.password = "Required";
    } else if (password.length < 8) {
      errors.password = "Min 8 chars";
    }

    if (password !== confirmPassword) {
      errors.confirmPassword = "No match";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setError("");
    setLoading(true);

    try {
      const payload = {
        business_name: businessName,
        subdomain,
        calling_code: callingCode.replace("+", ""),
        contact: callingCode + " " + contact,
        address,
        website,
        name,
        business_categories: category,
        email,
        password,
        password_confirmation: confirmPassword,
      };

      await register(payload);
      showToast.success("Success! Welcome to Glossa Cloud.");
      window.location.href = "/dashboard";
    } catch (err: any) {
      const errorData = err.response?.data;
      const serverErrors = errorData?.errors;

      if (serverErrors) {
        const newFieldErrors: Record<string, string> = {};
        const fieldMapping: Record<string, { label: string; stateKey: string }> = {
          business_name: { label: "Business Name", stateKey: "businessName" },
          subdomain: { label: "Subdomain", stateKey: "subdomain" },
          contact: { label: "Contact Number", stateKey: "contact" },
          address: { label: "Address", stateKey: "address" },
          name: { label: "Full Name", stateKey: "name" },
          email: { label: "Email Address", stateKey: "email" },
          password: { label: "Password", stateKey: "password" },
        };

        Object.entries(serverErrors).forEach(([key, messages]: [string, any]) => {
          const mapping = fieldMapping[key];
          const rawMessage = Array.isArray(messages) ? messages[0] : String(messages);
          let cleanMessage = rawMessage;
          if (rawMessage.toLowerCase().includes("duplicate") || rawMessage.toLowerCase().includes("taken")) {
            cleanMessage = `${mapping?.label || key} already in use.`;
          }
          if (mapping) newFieldErrors[mapping.stateKey] = cleanMessage;
        });

        if (Object.keys(newFieldErrors).length > 0) {
          setFieldErrors(newFieldErrors);
          setError("Check highlighted fields.");
        } else {
          setError(errorData?.message || "Registration failed.");
        }
      } else {
        setError(errorData?.message || "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4 py-8 ">
      <div className="w-full max-w-[680px] bg-white shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-gray-100 rounded-xl p-6 sm:p-8 transition-all hover:border-[#885ABB]/20">

        {/* Header Section */}
        <div className="flex flex-col items-center text-center w-full border-[#885ABB] mb-2 sm:mb-8">
          <img src="/glossa-purple.png" alt="Glossa" className="h-6 sm:h-7 w-auto mb-3 sm:mb-4" />
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">Register Business</h1>
          <p className="text-gray-400 text-xs sm:text-sm mt-1">Manage your operations with precision</p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4 sm:space-y-5">
          {/* Row 1: Section Headers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
            <h3 className="text-[10px] font-bold text-[#885ABB] uppercase tracking-[0.2em]  opacity-90 leading-tight">Business Identity</h3>
            <h3 className="hidden md:block text-[10px] font-bold text-[#885ABB] uppercase tracking-[0.2em] opacity-90 leading-tight">Account Security</h3>
          </div>

          <div className="space-y-4 sm:space-y-5">
            {/* Row 2: Business Name | Full Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 md:gap-y-0">
              <div className="space-y-1">
                <div className="relative group">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#885ABB] transition-colors" size={17} />
                  <input
                    type="text"
                    placeholder="Business Name"
                    value={businessName}
                    onChange={(e) => {
                      setBusinessName(e.target.value);
                      if (fieldErrors.businessName) setFieldErrors({ ...fieldErrors, businessName: "" });
                    }}
                    className={`w-full h-[46px] sm:h-[48px] py-0 pl-11 pr-4 bg-gray-50/50 border ${fieldErrors.businessName ? "border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.2)]" : "border-gray-100"} rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400`}
                  />
                </div>
                {fieldErrors.businessName && <p className="text-[11px] text-red-500 font-medium ml-1">{fieldErrors.businessName}</p>}
              </div>

              <div className="space-y-1">
                <div className="relative group">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#885ABB] transition-colors" size={17} />
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (fieldErrors.name) setFieldErrors({ ...fieldErrors, name: "" });
                    }}
                    className={`w-full h-[46px] sm:h-[48px] py-0 pl-11 pr-4 bg-gray-50/50 border ${fieldErrors.name ? "border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.2)]" : "border-gray-100"} rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400`}
                  />
                </div>
                {fieldErrors.name && <p className="text-[11px] text-red-500 font-medium ml-1">{fieldErrors.name}</p>}
              </div>
            </div>

            {/* Row 3: Subdomain | Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 md:gap-y-0">
              <div className="space-y-1">
                <div className="relative group">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#885ABB] transition-colors" size={17} />
                  <input
                    type="text"
                    placeholder="Subdomain"
                    value={subdomain}
                    onChange={(e) => {
                      setSubdomain(e.target.value);
                      if (fieldErrors.subdomain) setFieldErrors({ ...fieldErrors, subdomain: "" });
                    }}
                    className={`w-full h-[46px] sm:h-[48px] py-0 pl-11 pr-4 bg-gray-50/50 border ${fieldErrors.subdomain ? "border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.2)]" : "border-gray-100"} rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400`}
                  />
                </div>
                {fieldErrors.subdomain && <p className="text-[11px] text-red-500 font-medium ml-1">{fieldErrors.subdomain}</p>}
              </div>

              <div className="space-y-1">
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#885ABB] transition-colors" size={17} />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: "" });
                    }}
                    className={`w-full h-[46px] sm:h-[48px] py-0 pl-11 pr-4 bg-gray-50/50 border ${fieldErrors.email ? "border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.2)]" : "border-gray-100"} rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400`}
                  />
                </div>
                {fieldErrors.email && <p className="text-[11px] text-red-500 font-medium ml-1">{fieldErrors.email}</p>}
              </div>
            </div>

            {/* Row 4: Contact | Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 md:gap-y-0">
              <div className="space-y-1">
                <div className="flex gap-2">
                  <Select value={callingCode} onValueChange={setCallingCode}>
                    <SelectTrigger className="w-24 h-[46px] sm:h-[48px] bg-gray-50/50 border-gray-100 rounded-lg text-xs font-semibold focus:ring-[#885ABB] py-0 outline-none">
                      <SelectValue placeholder="+44" />
                    </SelectTrigger>
                    <SelectContent className="bg-white shadow-xl border border-gray-100">
                      {["+44", "+1", "+49", "+33", "+31"].map((code) => (
                        <SelectItem key={code} value={code} className="cursor-pointer text-[13px] hover:bg-gray-50 outline-none">{code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1 group">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#885ABB] transition-colors" size={17} />
                    <input
                      type="text"
                      placeholder="Contact No"
                      value={contact}
                      onChange={(e) => {
                        setContact(e.target.value);
                        if (fieldErrors.contact) setFieldErrors({ ...fieldErrors, contact: "" });
                      }}
                      className={`w-full h-[46px] sm:h-[48px] py-0 pl-11 pr-4 bg-gray-50/50 border ${fieldErrors.contact ? "border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.2)]" : "border-gray-100"} rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400`}
                    />
                  </div>
                </div>
                {fieldErrors.contact && <p className="text-[11px] text-red-500 font-medium ml-1">{fieldErrors.contact}</p>}
              </div>

              <div className="space-y-1">
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#885ABB] transition-colors" size={17} />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (fieldErrors.password) setFieldErrors({ ...fieldErrors, password: "" });
                    }}
                    className={`w-full h-[46px] sm:h-[48px] py-0 pl-11 pr-11 bg-gray-50/50 border ${fieldErrors.password ? "border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.2)]" : "border-gray-100"} rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400`}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#885ABB]">
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {fieldErrors.password && <p className="text-[11px] text-red-500 font-medium ml-1">{fieldErrors.password}</p>}
              </div>
            </div>

            {/* Row 5: Address | Confirm Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 md:gap-y-0">
              <div className="space-y-1">
                <div className="relative group">
                  <APIAddressSelect
                    value={address}
                    onChange={(val) => {
                      setAddress(val);
                      if (fieldErrors.address) setFieldErrors({ ...fieldErrors, address: "" });
                    }}
                    error={fieldErrors.address}
                    placeholder="Address"
                    icon={<MapPin size={17} />}
                  />
                </div>
                {fieldErrors.address && <p className="text-[11px] text-red-500 font-medium ml-1">{fieldErrors.address}</p>}
              </div>

              <div className="space-y-1">
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#885ABB] transition-colors" size={17} />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (fieldErrors.confirmPassword) setFieldErrors({ ...fieldErrors, confirmPassword: "" });
                    }}
                    className={`w-full h-[46px] sm:h-[48px] py-0 pl-11 pr-11 bg-gray-50/50 border ${fieldErrors.confirmPassword ? "border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.2)]" : "border-gray-100"} rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400`}
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#885ABB]">
                    {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {fieldErrors.confirmPassword && <p className="text-[11px] text-red-500 font-medium ml-1">{fieldErrors.confirmPassword}</p>}
              </div>
            </div>

            {/* Row 6: Category | Create Account */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 md:gap-y-0">
              <div className="space-y-1">
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="w-full h-[46px] sm:h-[48px] bg-gray-50/50 border-gray-100 rounded-lg text-sm transition-all focus:ring-1 focus:ring-[#885ABB] text-gray-600 outline-none py-0">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent className="bg-white shadow-xl border border-gray-100 p-1">
                    {[
                      { id: "1", label: "Hair & Styling" },
                      { id: "2", label: "Nail Salon" },
                      { id: "3", label: "Waxing" },
                      { id: "4", label: "Beauty" },
                      { id: "5", label: "Barbershop" },
                    ].map((cat) => (
                      <SelectItem key={cat.id} value={cat.id} className="cursor-pointer py-2.5 px-3 text-[13px] rounded-md outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50">
                        <span className="flex items-center justify-between w-full">
                          {cat.label}
                          <SelectItemIndicator><Check className="w-4 h-4 text-[#885ABB]" /></SelectItemIndicator>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#885ABB] to-[#7647a8] text-white h-[46px] sm:h-[48px] rounded-lg font-semibold text-sm shadow-md shadow-[#885ABB]/10 hover:shadow-lg hover:opacity-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed tracking-widest"
                >
                  {loading ? "Creating..." : "Create Account"}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 text-red-600 bg-red-50 text-[12px] rounded-lg border border-red-100 animate-in fade-in slide-in-from-top-1">
              <p className="font-medium leading-tight">{error}</p>
            </div>
          )}
        </form>

        <div className="relative my-7 sm:my-8 text-center">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-100"></span></div>
          <div className="relative flex justify-center text-[10px] uppercase font-bold tracking-widest"><span className="bg-white px-4 text-gray-300">OR</span></div>
        </div>

        <p className="text-center text-[13px] text-gray-400 font-medium">
          Already have an account?{" "}
          <a href="/login" className="text-blue-500 hover:text-blue-600 hover:underline ml-1 transition-colors font-semibold">
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
}
